/* eslint-disable react-refresh/only-export-components */
import { useCallback } from 'react';
import { DropdownOption, Heading } from '@hawkins/components';
import { Grid } from 'src/domains/shared/components/Grid';

import * as FramingAndWorkingResolutionCalculators from './FramingAndWorkingResolutionCalculators';
import * as MinimumAnamorphicCaptureResolution from './MinimumAnamorphicCaptureResolution';

/**
 * TODO: figure out a way to minimize this repetition
 * but still keep it type safe
 */
export type CalculatorInputMap = {
  [FramingAndWorkingResolutionCalculators.KEY]?: FramingAndWorkingResolutionCalculators.FormState;
  [MinimumAnamorphicCaptureResolution.KEY]?: MinimumAnamorphicCaptureResolution.FormState;
};

export type CalculatorOutputMap = {
  [FramingAndWorkingResolutionCalculators.KEY]?: FramingAndWorkingResolutionCalculators.CalculatorOutputProps;
  [MinimumAnamorphicCaptureResolution.KEY]?: MinimumAnamorphicCaptureResolution.CalculatorOutputProps;
};

export type CalculatorBag =
  | FramingAndWorkingResolutionCalculators.CalculatorBag
  | MinimumAnamorphicCaptureResolution.CalculatorBag;

export type CalculatorKey =
  | typeof FramingAndWorkingResolutionCalculators.KEY
  | typeof MinimumAnamorphicCaptureResolution.KEY;

type CalculatorOutputProps =
  | FramingAndWorkingResolutionCalculators.CalculatorOutputProps
  | MinimumAnamorphicCaptureResolution.CalculatorOutputProps;

type CalculatorFormState =
  | FramingAndWorkingResolutionCalculators.FormState
  | MinimumAnamorphicCaptureResolution.FormState;

export type CalculatorInputChangeEventHandler = (
  key: CalculatorKey,
  input: CalculatorFormState,
  output: CalculatorOutputProps
) => void;

export type DropdownCalculatorOption = DropdownOption<CalculatorKey>;

export const CalculatorMap: Record<
  CalculatorKey,
  DropdownOption<CalculatorBag>
> = {
  [FramingAndWorkingResolutionCalculators.KEY]: {
    label: FramingAndWorkingResolutionCalculators.LABEL,
    value: FramingAndWorkingResolutionCalculators.calculatorBag,
  },
  [MinimumAnamorphicCaptureResolution.KEY]: {
    label: MinimumAnamorphicCaptureResolution.LABEL,
    value: MinimumAnamorphicCaptureResolution.calculatorBag,
  },
};

export const calculatorOptions: DropdownCalculatorOption[] = Object.values(
  CalculatorMap
).map(({ label, value }) => ({
  label,
  value: value.key,
}));

interface UseCalculatorComponentsHookProps {
  inputMap: CalculatorInputMap;
  outputMap: CalculatorOutputMap;
  onInputReset: (key: CalculatorKey) => void;
  onInputChange: CalculatorInputChangeEventHandler;
}

const DefaultFallback = () => (
  <Grid gap="Space8" alignContent="center">
    <Heading level={2} align="center">
      Select a camera to get started.
    </Heading>
  </Grid>
);

export const useCalculatorComponents = ({
  inputMap,
  outputMap,
  onInputReset,
  onInputChange,
}: UseCalculatorComponentsHookProps) => {
  const getComponentsForOption = useCallback(
    (option: DropdownOption<CalculatorBag>) => {
      const { key, Input, Output, Description } = option.value;
      const output = outputMap[key as keyof CalculatorOutputMap];
      const inputValues = inputMap[key as keyof CalculatorInputMap];

      const inputProps =
        key === FramingAndWorkingResolutionCalculators.KEY
          ? {
              onReset: onInputReset,
              onChange: onInputChange,
              initialValues:
                inputValues as FramingAndWorkingResolutionCalculators.FormState,
            }
          : {
              onChange: onInputChange,
              initialValues:
                inputValues as MinimumAnamorphicCaptureResolution.FormState,
            };

      return {
        description: <Description />,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        input: <Input key={key} {...(inputProps as any)} />,
        output: output ? (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <Output {...(output as any)} />
        ) : (
          <DefaultFallback />
        ),
      };
    },
    [outputMap, inputMap, onInputChange, onInputReset]
  );

  return {
    getComponentsForOption,
  };
};
